// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import keys from 'lodash/keys';
import each from 'lodash/each';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles, Button } from '@material-ui/core';
import { getI18n } from "../../../utils/intl";

import {
  FILTER_TYPE_RANK,
  FILTER_TYPE_NAME,
  FILTER_TYPE_URI,
  FILTER_TYPE_CODE,
  FILTER_TYPE_RANGE,
} from '../../../constants/filter';
import { getSearchPlaceHolderText } from '../utils/filter';

import FilterByRank from '../FilterByRank';
import FilterByName from '../FilterByName';
import FilterByValue from '../FilterByValue';
import FilterByRange from '../FilterByRange';
import FilterByURI from '../FilterByURI';

import styles from './styles';

const getCodesArray = (codeMap) => {
  const codesList = [];
  each(keys(codeMap), (code) => {
    const obj = codeMap[code];
    codesList[obj.index] = code;
  });
  return codesList;
};

const CustomFilter = (props) => {
  const {
    filterInitState, entityNameMap, selectedFilterType, maxAllowedEntities, isGroupedSelected,
    populateData, showFilter, filterTypeIndex, entityType, handleSearchFilterNameChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    data: [],
    filterType: FILTER_TYPE_RANK,
    selectedItems: {},
    selectedCodes: {},
    selectedRank: 1,
    areCodesValid: true,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    if (filterInitState) {
      if (filterInitState.filterType === FILTER_TYPE_RANK) {
        setState({
          filterType: filterInitState.filterType,
          selectedRank: filterInitState.rank,
        });
      } else if (filterInitState.filterType === FILTER_TYPE_NAME) {
        setState({
          filterType: filterInitState.filterType,
          selectedItems: Object.assign(
            {},
            filterInitState.selectedItems,
          ),
        });
      }
    }
  }, []);

  useEffect(() => {
    if (state.filterTypeIndex === filterTypeIndex) {
      return;
    }
    if (!filterInitState) return
    let selectedItems = filterInitState.selectedItems;
    let selectedCodes = {};
    if (filterInitState.filterType === FILTER_TYPE_CODE) {
      selectedCodes = getCodesMap(keys(filterInitState.selectedItems));
      selectedItems = [];
    }
    setState({
      showFilter,
      filterType: selectedFilterType,
      selectedRank:
        filterInitState && filterInitState.rank ? filterInitState.rank : 1,
      selectedItems,
      selectedCodes,
      filterTypeIndex,
      filterInitState,
    });
  }, [showFilter, filterTypeIndex, filterInitState, selectedFilterType]);

  const getCodesMap = (selectedItems) => {
    const codes = {};
    let areCodesValid = true;
    each(selectedItems, (item, index) => {
      if (codes[item]) {
        areCodesValid = false;
      } else {
        codes[item] = { index };
      }
    });
    setState({ areCodesValid });
    return codes;
  }
  
  const checkValidityOfCodes = (codes) => {
    let valid = true;
    each(keys(codes) || [], (code) => {
      valid = valid && validateRespCode(code);
    });
    return valid;
  }

  const calculateCount = (updatedState) => {
    const {
      filterType,
      selectedRank,
      selectedItems,
      selectedCodes,
      areCodesValid,
      isInvalidRankSize,
    } = updatedState;
    let selectedEntities = 0;
    let isValidCodes = true;
    if (filterType === FILTER_TYPE_RANK) {
      if (!isInvalidRankSize) {
        selectedEntities = selectedRank;
      }
    } else if (
      filterType === FILTER_TYPE_NAME ||
      filterType === FILTER_TYPE_URI ||
      filterType === FILTER_TYPE_RANGE
    ) {
      if (isGroupedSelected) {
        selectedEntities = keys(selectedItems).length ? 1 : 0;
      } else {
        selectedEntities = keys(selectedItems).length;
      }
    } else if (filterType === FILTER_TYPE_CODE) {
      if (isGroupedSelected) {
        selectedEntities = keys(selectedCodes).length ? 1 : 0;
      } else {
        let codes = keys(selectedCodes);
        codes = filter(codes, (code) => !isEmpty(code));
        selectedEntities = codes.length;
      }
      isValidCodes = checkValidityOfCodes(selectedCodes) && areCodesValid;
    }
    populateData(filterType,
      !(selectedEntities > 0 && selectedEntities <= maxAllowedEntities) || !isValidCodes,
      selectedEntities,
      filterType === FILTER_TYPE_CODE ? selectedCodes : selectedItems,
    );
  };

  const updateRankSize = (selectedRank, isInvalid) => {
    setState({ selectedRank, isInvalidRankSize: isInvalid });
    populateData(state.filterType, isInvalid, selectedRank);
  };

  const updateSelectedItems = selectedItems => {
    let updatedState = {};
    if (state.filterType === FILTER_TYPE_CODE) {
      updatedState = {
        selectedCodes: getCodesMap(selectedItems),
      };
    } else {
      updatedState = {
        selectedItems,
      };
    }
    setState(updatedState);
    calculateCount({
      ...state,
      ...updatedState,
    });
  };

  const updateUriValueList = (isInvalid, valueList) => {
    setState({ selectedItems: valueList });
    populateData(state.filterType, isInvalid, valueList.length, valueList);
  };

  const toggleFilterType = filterType => {
    const updatedState = {
      filterType,
    };
    setState(updatedState);
    calculateCount({
      ...state,
      ...updatedState,
    });
  };

  const validateRespCode = (value) => {
    const pattern = new RegExp('^\\d{3}$');
    if (pattern.test(value) &&
      value >= 200 && value <= 599) {
      return true;
    }
    return false;
  }

  const renderFilterContainer = () => {
    const placeHolderText = getSearchPlaceHolderText(entityType, intl);
    switch (state.filterType) {
      case FILTER_TYPE_NAME: {
        const showSecondaryText = entityType === 'apiKey';
        return (
          <FilterByName
            filterData={entityNameMap}
            placeHolderText={placeHolderText}
            selectedItems={state.selectedItems}
            updateSelectedItems={updateSelectedItems}
            maxAllowedEntities={maxAllowedEntities}
            hideCount
            showSecondaryText={showSecondaryText}
            handleSearchFilterNameChange={handleSearchFilterNameChange}
          />
        );
      }
      case FILTER_TYPE_RANK: return (
        <FilterByRank
          updateRankSize={updateRankSize}
          updatedFilterRankSize={state.selectedRank}
          maxAllowedEntities={maxAllowedEntities}
          hideCount
        />);
      case FILTER_TYPE_CODE: return (
        <FilterByValue
          valuesHeading={'label.analytics.http.status.code.text'}
          validate={validateRespCode}
          isGrouped={isGroupedSelected}
          values={getCodesArray(state.selectedCodes)}
          updateSelectedItems={updateSelectedItems}
          validationError={'label.analytics.customReport.response.invalid.code.error.text'}
          duplicateError={'label.analytics.customReport.response.duplicate.code.error.text'}
        />);
      case FILTER_TYPE_URI: return (
        <FilterByURI
          data={state.selectedItems}
          updateData={updateUriValueList}
          isGrouped={isGroupedSelected}
        />);
      case FILTER_TYPE_RANGE: return (
        <FilterByRange
          filterData={entityNameMap}
          selectedItems={state.selectedItems}
          updateSelectedItems={updateSelectedItems}
          maxAllowedEntities={maxAllowedEntities}
          hideCount
        />);
      default: break;
    }
    return null;
  }
  return showFilter ? (
    <div className={classes.filter} id={'filter-container'}>
      <div className={classes.filterTypeContainer}>
        <span>
          <Button
            onClick={() => toggleFilterType(FILTER_TYPE_RANK)}
            className={clsx(
              classes.filterTypeBtn,
              state.filterType === FILTER_TYPE_RANK
                ? classes.filterTypeBtnSelected
                : null,
            )}
          >
            {intl.getI18nMessage('label.analytics.filter.type.filterByRank.label')}
          </Button>
        </span>
        {filterTypeIndex <= 4 || filterTypeIndex === 7 ? (
          <span className={classes.separatedBtn}>
            <Button
              onClick={() => toggleFilterType(FILTER_TYPE_NAME)}
              className={clsx(
                classes.filterTypeBtn,
                state.filterType === FILTER_TYPE_NAME
                  ? classes.filterTypeBtnSelected
                  : null,
              )}
              data-apim-test={'filter-by-name'}
            >
              {intl.getI18nMessage('label.analytics.filter.type.filterByName.label')}
            </Button>
          </span>
        ) : null}
        {filterTypeIndex === 5 ? (
          <span>
            <span className={classes.separatedBtn}>
              <Button
                onClick={() => toggleFilterType(FILTER_TYPE_RANGE)}
                className={clsx(
                  classes.filterTypeBtn,
                  state.filterType === FILTER_TYPE_RANGE
                    ? classes.filterTypeBtnSelected
                    : null,
                )}
              >
                {intl.getI18nMessage('label.analytics.filter.type.filterByResponseRange.label')}
              </Button>
            </span>
            <span className={classes.separatedBtn}>
              <Button
                onClick={() => toggleFilterType(FILTER_TYPE_CODE)}
                className={clsx(
                  classes.filterTypeBtn,
                  state.filterType === FILTER_TYPE_CODE
                    ? classes.filterTypeBtnSelected
                    : null,
                )}
              >
                {intl.getI18nMessage('label.analytics.filter.type.filterByResponseCode.label')}
              </Button>
            </span>
          </span>
        ) : null}
        {filterTypeIndex === 6 ? (
          <span className={classes.separatedBtn}>
            <Button
              onClick={() => toggleFilterType(FILTER_TYPE_URI)}
              className={clsx(
                classes.filterTypeBtn,
                state.filterType === FILTER_TYPE_URI
                  ? classes.filterTypeBtnSelected
                  : null,
              )}
            >
              {intl.getI18nMessage('label.analytics.filter.type.filterByUriExpression.label')}
            </Button>
          </span>
        ) : null}
      </div>
      {renderFilterContainer()}
    </div>
  ) : null;
}

CustomFilter.propTypes = {
  entityNameMap: PropTypes.object,
  entityType: PropTypes.string,
  filterInitState: PropTypes.object,
  showFilter: PropTypes.bool,
  maxAllowedEntities: PropTypes.number,
  filterTypeIndex: PropTypes.number,
  populateData: PropTypes.func,
  isGroupedSelected: PropTypes.bool,
  handleFilterChange: PropTypes.func,
  selectedFilterType: PropTypes.string,
  handleSearchFilterNameChange: PropTypes.func,
};

export default CustomFilter;
